import { Field } from './Field.js';
import { DisplayNameField, checkDisplayNameError } from './DisplayNameField.js';
import { EmailField } from './EmailField.js';
import { checkEmail } from './emailValidation.js';
import { PasswordField } from '../PasswordField.js';
import { RadioGroup, CheckboxGroup, StateSelect } from './inputs.js';
import { LegalLinks } from './LegalLinks.js';
import type { WizardStep } from './RegistrationWizard.js';
import {
  LEARNER_AGE_OPTIONS, HEARD_ABOUT_OPTIONS, LEARNING_TIMES,
  IMMERSION_REASONS, INTERPRETER_FIELDS,
} from './types.js';

const basicInfo: WizardStep = {
  key: 'basic', title: 'Basic information',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!d.fullName.trim()) e.fullName = 'Required';
    if (d.password.length < 6) e.password = 'At least 6 characters';
    if (!d.displayName.trim()) e.displayName = 'Required';
    if (!d.ageRange) e.ageRange = 'Select an age range';
    if (!d.email.trim()) e.email = 'Required';
    else {
      const msg = checkEmail(d.email).error;
      if (msg) e.email = msg;
    }
    if (!d.city?.trim()) e.city = 'Required';
    if (!d.state) e.state = 'Required';
    if (!d.heardAbout) e.heardAbout = 'Select one';
    if (d.heardAbout === 'other' && !d.heardAboutOther?.trim()) e.heardAboutOther = 'Please specify';
    return e;
  },
  validateAsync: async (d): Promise<Record<string, string>> => {
    const msg = await checkDisplayNameError(d.displayName);
    return msg ? { displayName: msg } : {};
  },
  Component: ({ data, update, errors }) => (
    <>
      <Field label="Full name" error={errors.fullName}>
        <input className="input" value={data.fullName} onChange={(e) => update({ fullName: e.target.value })} />
      </Field>
      <PasswordField label="Password" value={data.password} error={errors.password}
        onChange={(v) => update({ password: v })} autoComplete="new-password" minLength={6} />
      <DisplayNameField value={data.displayName} update={update} error={errors.displayName} />
      <RadioGroup legend="Age range (must be 14 or older)" options={LEARNER_AGE_OPTIONS}
        value={data.ageRange} onChange={(id) => update({ ageRange: id as typeof data.ageRange })} error={errors.ageRange} />
      <EmailField label="Email" value={data.email} error={errors.email}
        onChange={(v) => update({ email: v })} autoComplete="email" />
      <Field label="Phone (optional)"><input className="input" value={data.phone ?? ''} onChange={(e) => update({ phone: e.target.value })} /></Field>
      <Field label="City" error={errors.city}>
        <input className="input" value={data.city ?? ''} onChange={(e) => update({ city: e.target.value })} />
      </Field>
      <Field label="State" error={errors.state}>
        <StateSelect value={data.state ?? ''} onChange={(id) => update({ state: id })} />
      </Field>
      <Field label="Ethnicity (optional)" hint="Used only for aggregate community data; never shared publicly.">
        <input className="input" value={data.ethnicity ?? ''} onChange={(e) => update({ ethnicity: e.target.value })} />
      </Field>
      <RadioGroup legend="How did you hear about SignWow?" options={HEARD_ABOUT_OPTIONS}
        value={data.heardAbout ?? ''} onChange={(id) => update({ heardAbout: id })} error={errors.heardAbout} />
      {data.heardAbout === 'other' ? (
        <Field label="Please specify" error={errors.heardAboutOther}>
          <input className="input" value={data.heardAboutOther ?? ''} onChange={(e) => update({ heardAboutOther: e.target.value })} />
        </Field>
      ) : null}
    </>
  ),
};

const goals: WizardStep = {
  key: 'goals', title: 'Learning goals & availability',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!(d.preferredTimes ?? []).length) e.preferredTimes = 'Pick at least one';
    const reasons = d.immersionReasons ?? [];
    if (!reasons.length) e.immersionReasons = 'Pick at least one';
    if (reasons.includes('other') && !d.immersionReasonOther?.trim()) e.immersionReasonOther = 'Please specify';
    if (d.wantsInterpreter === undefined) e.wantsInterpreter = 'Please choose';
    // Both interpreter checks only apply to the Yes branch — its inputs are off
    // screen otherwise, and an error on a hidden field is an unexplained block.
    if (d.wantsInterpreter === true) {
      const fields = d.interpreterFields ?? [];
      if (!fields.length) e.interpreterFields = 'Select at least one field';
      if (fields.includes('other') && !d.interpreterFieldOther?.trim()) e.interpreterFieldOther = 'Please specify';
    }
    return e;
  },
  Component: ({ data, update, errors }) => {
    const times = data.preferredTimes ?? [];
    const reasons = data.immersionReasons ?? [];
    const fields = data.interpreterFields ?? [];
    const toggleTime = (id: string) => update({ preferredTimes: times.includes(id) ? times.filter((x) => x !== id) : [...times, id] });
    const toggleReason = (id: string) => update({ immersionReasons: reasons.includes(id) ? reasons.filter((x) => x !== id) : [...reasons, id] });
    const toggleField = (id: string) => update({ interpreterFields: fields.includes(id) ? fields.filter((x) => x !== id) : [...fields, id] });
    // Answering No drops the Yes-branch answers so they can't reach the database
    // behind wants_interpreter: false.
    const setWantsInterpreter = (id: string) => update(id === 'yes'
      ? { wantsInterpreter: true }
      : { wantsInterpreter: false, interpreterFields: [], interpreterFieldOther: '' });
    return (
      <>
        <CheckboxGroup legend="Preferred learning times" options={LEARNING_TIMES} values={times} onToggle={toggleTime} error={errors.preferredTimes} />
        <CheckboxGroup legend="Why do you want to immerse in ASL?" options={IMMERSION_REASONS} values={reasons} onToggle={toggleReason} error={errors.immersionReasons} />
        {reasons.includes('other') ? (
          <Field label="Please specify" error={errors.immersionReasonOther}>
            <input className="input" value={data.immersionReasonOther ?? ''} onChange={(e) => update({ immersionReasonOther: e.target.value })} />
          </Field>
        ) : null}
        <RadioGroup legend="Do you want to become an interpreter?"
          options={[{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }]}
          value={data.wantsInterpreter === undefined ? '' : data.wantsInterpreter ? 'yes' : 'no'}
          onChange={setWantsInterpreter} error={errors.wantsInterpreter} />
        {data.wantsInterpreter === true ? (
          <>
            <CheckboxGroup legend="Which fields? (select all that apply)" options={INTERPRETER_FIELDS}
              values={fields} onToggle={toggleField} error={errors.interpreterFields} />
            {fields.includes('other') ? (
              <Field label="Please specify" error={errors.interpreterFieldOther}>
                <input className="input" value={data.interpreterFieldOther ?? ''} onChange={(e) => update({ interpreterFieldOther: e.target.value })} />
              </Field>
            ) : null}
          </>
        ) : null}
        <Field label="Short goal (optional)">
          <input className="input" placeholder="I want to be able to…" value={data.goal ?? ''} onChange={(e) => update({ goal: e.target.value })} />
        </Field>
      </>
    );
  },
};

const planAgreement: WizardStep = {
  key: 'plan', title: 'Agreement',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!d.agreementAccepted) e.agreementAccepted = 'You must accept to continue';
    return e;
  },
  Component: ({ data, update, errors }) => {
    return (
      <>
        <p className="hint">You'll pick and pay for a credit package after you sign in.</p>

        <div className="flex flex-col gap-2">
          <label className="inline-flex items-start gap-2">
            <input type="checkbox" aria-label="I agree" checked={data.agreementAccepted ?? false} onChange={(e) => update({ agreementAccepted: e.target.checked })} />
            <span className="hint">Yes, I have read and agree to the Privacy Policy &amp; Terms of Use.</span>
          </label>
          <LegalLinks />
          {errors.agreementAccepted ? <span className="text-[0.8rem] text-status-error dark:text-red-400">{errors.agreementAccepted}</span> : null}
        </div>

        <p className="hint">
          By clicking “Submit Registration,” you acknowledge that your name and the date will serve as your digital signature.
        </p>

      </>
    );
  },
};

export const learnerCoreSteps: WizardStep[] = [basicInfo, goals, planAgreement];
